import { useParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

import { AxiosError, AxiosResponse } from 'axios';

import { useFeatureFlag } from '../../../shared/UserActivityTracking/useFeatureFlag';
import { QUOTE } from '../constants';
import { DownloadByVersionIdTypes } from '../queries/apiTypes';
import { useDownloadMutation } from '../queries';
import { SnackbarMessages } from '../constants';
import { CED_NET_DOWNLOAD_FAILED, DOWNLOAD_FAILED_MSG } from '../../../shared/constants';
import { callProxyURL } from '../utils/downloadFile';
import { IBomAndQuoteIds } from './types';
import { useAllTagsHook } from 'src/features/shared/hooks/useAllTagsHook';
import { STATUS_RECONCILIATION_POPUP_CONSTANTS } from 'src/features/shared/UpdateProjectQuoteStatusModal/constants';
import { useGetProjectBomsQuery, useGetProjectDetailsQuery } from 'src/features/Project/queries';
import { useDocumentTags } from 'src/features/Dashboard/queries';
import { getValueKeyFromMap } from 'src/features/shared/UpdateProjectQuoteStatusModal/utils';

type OptionsType = 'share' | 'download';

interface IShareOrDownloadOptionsType {
    documentType: string;
    setSnackbarOpen: (message: string, autoHideDuration?: number | null | undefined) => void;
    onClick?: () => void;
    bomAndQuoteIds?: Array<IBomAndQuoteIds>;
    quoteId?: number;
    type?: OptionsType;
}

export const useGetShareOrDownloadOptions = ({ onClick = () => { }, quoteId, setSnackbarOpen, documentType, type = 'share', bomAndQuoteIds }: IShareOrDownloadOptionsType) => {
    const { bomId = '' } = useParams();
    const { enable_cednet_format, enable_epicor_format } = useFeatureFlag();

    const { mutateAsync, isLoading } = useDownloadMutation();

    const handleError = useCallback(
        (fileType: DownloadByVersionIdTypes['type'], error: unknown) => {
            const axiosError = error as AxiosError<{ message: string }>;

            if ((fileType === 'ced_xlsx' || fileType === 'jm_xlsx') && axiosError?.response?.status === 404) {
                setSnackbarOpen(axiosError?.response.data?.message ?? CED_NET_DOWNLOAD_FAILED);
            } else {
                setSnackbarOpen(DOWNLOAD_FAILED_MSG);
            }
        },
        [setSnackbarOpen]
    );

    const handleSingleDownload = useCallback(
        async (fileType?: DownloadByVersionIdTypes['type']) => {
            const currentFileType = fileType ? fileType : 'flattened';
            try {
                setSnackbarOpen(SnackbarMessages.DOWNLOADING);
                if (quoteId && documentType) {
                    const response = await mutateAsync({ bomId, id: quoteId, type: currentFileType, documentType });
                    await callProxyURL(response?.data.url, fileType ? fileType : 'pdf', response?.data.filename);
                    setSnackbarOpen(SnackbarMessages.DOWNLOAD_SUCCESS);
                }
            } catch (error) {
                handleError(currentFileType, error);
            }
        },
        [setSnackbarOpen, quoteId, documentType, mutateAsync, bomId, handleError]
    );

    const handleMutlipleDownloads = useCallback(
        async (fileType?: DownloadByVersionIdTypes['type']) => {
            const currentFileType = fileType ? fileType : 'flattened';
            try {
                if (bomAndQuoteIds?.length) {
                    setSnackbarOpen(SnackbarMessages.DOWNLOADING);
                    const mutationsPromiseArr: Array<Promise<AxiosResponse>> = bomAndQuoteIds.map((item) =>
                        mutateAsync({
                            bomId: item.bomId,
                            id: item.quoteId,
                            type: currentFileType,
                            documentType
                        })
                    );
                    const response = await Promise.all(mutationsPromiseArr);
                    if (response?.length) {
                        await Promise.all([
                            ...response.map((item) => callProxyURL(item?.data.url, fileType ? fileType : 'pdf', item?.data.filename))
                        ]);
                    }
                    setSnackbarOpen(SnackbarMessages.DOWNLOAD_SUCCESS);
                }
            } catch (error) {
                handleError(currentFileType, error);
            }
        },
        [bomAndQuoteIds, documentType, handleError, mutateAsync, setSnackbarOpen]
    );

    const getDownloadOption = useCallback(
        (label: string, type?: DownloadByVersionIdTypes['type']) => {
            if (bomAndQuoteIds?.length) {
                return {
                    label,
                    onClick: () => handleMutlipleDownloads(type)
                };
            }
            return {
                label,
                onClick: () => handleSingleDownload(type)
            };
        },
        [bomAndQuoteIds, handleSingleDownload, handleMutlipleDownloads]
    );

    const shareDocumentOption = useMemo(
        () => ({
            label: 'Share via Email',
            onClick
        }),
        [onClick]
    );

    const optionsArray = useMemo(() => {
        const optionsArray = [];
        const labelPrefix = type === 'share' ? 'Download as ' : '';
        if (type === 'share') {
            optionsArray.push(shareDocumentOption);
        }
        optionsArray.push(getDownloadOption(`${labelPrefix}PDF`));

        if (documentType === QUOTE) {
            optionsArray.push(getDownloadOption(`${labelPrefix}Excel`, 'xlsx'));
        }

        if (enable_cednet_format && documentType === QUOTE) {
            optionsArray.push(getDownloadOption(`${labelPrefix}CEDNet`, 'ced_xlsx'));
        }

        if (enable_epicor_format && documentType === QUOTE) {
            optionsArray.push(getDownloadOption(`${labelPrefix}Job Management (Internal)`, 'jm_xlsx'));
        }

        return optionsArray;
    }, [enable_cednet_format, enable_epicor_format, documentType, type, shareDocumentOption, getDownloadOption]);

    return { optionsArray, isLoading };
};

export const useValidateDocumentStatusBeforeUpdate = (projectId: string) => {
    const { allTagsObject } = useAllTagsHook();
    const { data: projectDetails } = useGetProjectDetailsQuery(Number(projectId));
    const { data: projectBoms } = useGetProjectBomsQuery(Number(projectId), {
        enabled: Boolean(projectId)
    });
    const { data: quotesTagData } = useDocumentTags(QUOTE);

    const countOfWinningBoms = useMemo(() => {
        let winnningBomsCount = 0;
        if (projectBoms?.data && quotesTagData?.data && allTagsObject.size) {
            const winTagId = getValueKeyFromMap(allTagsObject, STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON);
            const bomQuotesTagMap = new Map();
            quotesTagData.data.forEach((item) => {
                bomQuotesTagMap.set(item.bom_id, item.tag_id);
            });

            for (let item of projectBoms.data) {
                if (bomQuotesTagMap.has(item.id) && bomQuotesTagMap.get(item.id) === winTagId) {
                    winnningBomsCount += 1;
                }
            }
        }

        return winnningBomsCount;
    }, [projectBoms?.data, quotesTagData?.data, allTagsObject])

    const validateDocumentStatus = useCallback((documentStatusId: number): {
        canUpdate: boolean,
        snackbarMsg: string
    } => {
        const documentTag = documentStatusId === 0 ? '-' : allTagsObject.get(documentStatusId);
        const currentProjectStage = projectDetails?.data?.stage || '';
        const result = {
            canUpdate: true,
            snackbarMsg: ''
        }

        if (documentTag) {
            switch (currentProjectStage) {
                case STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_STAGE_CLOSED_LOST:
                    if (documentTag === STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON || documentTag === '-') {
                        result.snackbarMsg = STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_CLOSED_LOST_ERROR_MSG;
                        result.canUpdate = false;
                    }
                    break;

                case STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_STAGE_NO_BID:
                    if (documentTag === STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON || documentTag === '-') {
                        result.snackbarMsg = STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_CLOSED_ABANDONED_ERROR_MSG;
                        result.canUpdate = false;
                    }
                    break;

                case STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_STAGE_CLOSED_WON:
                    if ((countOfWinningBoms < 2 && documentTag !== STATUS_RECONCILIATION_POPUP_CONSTANTS.BUTTON_TAGS.WON) || documentTag === '-') {
                        result.snackbarMsg = STATUS_RECONCILIATION_POPUP_CONSTANTS.PROJECT_CLOSED_WON_ERROR_MSG;
                        result.canUpdate = false;
                    }
                    break;

                default:
                    break;
            }
        }


        return result
    }, [allTagsObject, countOfWinningBoms, projectDetails?.data?.stage])

    return {
        validateDocumentStatus
    }
}
